import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from '../utils/SimpleRouter';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/apiUrl';

const API = getApiUrl();

const SubmitMessage = () => {
  const { qrCodeId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    author_email: '',
    start_date: new Date().toISOString().slice(0, 16), // Default to now with time
    end_date: '',
    tag: null
  });
  const [loading, setLoading] = useState(false);
  const [screens, setScreens] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [showAllScreens, setShowAllScreens] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [organizationName, setOrganizationName] = useState('');

  useEffect(() => {
    fetchScreens();
    fetchTags();
  }, [qrCodeId]);

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API}/screens/by-qr/${qrCodeId}`);
      setScreens(response.data);
      // Default: select all screens
      setSelectedScreens(response.data.map(s => s.id));
    } catch (error) {
      console.error('Failed to fetch screens:', error);
    }
  };

  const fetchTags = async () => {
    try {
      // Use the QR code ID directly for the display endpoint
      const orgResponse = await axios.get(`${API}/display/${qrCodeId}`);
      setAvailableTags(orgResponse.data.organization?.message_tags || []);
      setOrganizationName(orgResponse.data.organization?.name || '');
      console.log('📋 Loaded organization:', orgResponse.data.organization?.name);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleScreenToggle = (screenId) => {
    setShowAllScreens(false);
    if (selectedScreens.includes(screenId)) {
      setSelectedScreens(selectedScreens.filter(id => id !== screenId));
    } else {
      setSelectedScreens([...selectedScreens, screenId]);
    }
  };

  const handleShowAllScreens = () => {
    setShowAllScreens(!showAllScreens);
    if (!showAllScreens) {
      setSelectedScreens(screens.map(s => s.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.end_date) {
      toast.error('Vennligst velg en sluttdato');
      return;
    }
    
    setLoading(true);

    if (selectedScreens.length === 0) {
      toast.error('Vennligst velg minst én skjerm');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/messages/submit`, {
        ...formData,
        qr_code_id: qrCodeId,
        screen_ids: selectedScreens,
        start_date: formData.start_date,
        end_date: formData.end_date
      });
      
      toast.success('Melding sendt! Du vil motta en bekreftelse på e-post.');
      setTimeout(() => {
        navigate(`/mobile/${qrCodeId}`);
      }, 1500);
    } catch (error) {
      console.error('Submit error:', error.response || error);
      toast.error(error.response?.data?.detail || 'Kunne ikke sende melding. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="submit-page">
      <div className="submit-container">
        {organizationName && (
          <div style={{
            background: '#50E3C2',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.1rem',
              color: '#FFFFFF',
              fontWeight: '600'
            }}>
              {organizationName}
            </h2>
          </div>
        )}
        <h1>Send melding til denne skjermen</h1>
        <p className="subtitle">Fyll ut skjemaet under for å legge til en melding</p>
        
        <form onSubmit={handleSubmit} data-testid="submit-form">
          <div className="form-group">
            <label htmlFor="title">Tittel *</label>
            <input
              type="text"
              id="title"
              data-testid="title-input"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Melding *</label>
            <textarea
              id="message"
              data-testid="message-input"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              required
              maxLength={500}
              rows={3}
            />
            <small>{formData.message.length}/500 tegn</small>
          </div>

          {availableTags && availableTags.length > 0 && (
            <div className="form-group">
              <label>Velg kategori (valgfritt)</label>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '0.5rem',
                marginTop: '0.5rem'
              }}>
                {availableTags.map((tag, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setFormData({...formData, tag: formData.tag === tag.name ? null : tag.name})}
                    style={{
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.85rem',
                      borderRadius: '12px',
                      border: formData.tag === tag.name ? `2px solid ${tag.color}` : `2px solid ${tag.color}`,
                      background: formData.tag === tag.name ? tag.color : `${tag.color}20`,
                      color: formData.tag === tag.name ? tag.text_color : tag.color,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: formData.tag === tag.name ? '600' : '500',
                      opacity: formData.tag === tag.name ? 1 : 0.8
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
              <small style={{ marginTop: '0.5rem', display: 'block' }}>
                Klikk på en TAG for å kategorisere meldingen din
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">E-postadresse * (kun for verifisering)</label>
            <input
              type="email"
              id="email"
              data-testid="email-input"
              value={formData.author_email}
              onChange={(e) => setFormData({...formData, author_email: e.target.value})}
              required
            />
            <small>Din e-post vises ikke offentlig</small>
          </div>

          <div className="form-group">
            <label htmlFor="start_date">Startdato og klokkeslett *</label>
            <input
              type="datetime-local"
              id="start_date"
              value={formData.start_date}
              onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_date">Sluttdato og klokkeslett *</label>
            <input
              type="datetime-local"
              id="end_date"
              data-testid="end-date-input"
              value={formData.end_date}
              onChange={(e) => setFormData({...formData, end_date: e.target.value})}
              min={formData.start_date}
              required
            />
            <small>Meldingen vil automatisk fjernes etter denne datoen</small>
          </div>

          {screens.length > 1 && (
            <div className="form-group">
              <label>Velg hvilke skjermer meldingen skal vises på *</label>
              
              <div className="screen-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={showAllScreens}
                    onChange={handleShowAllScreens}
                  />
                  <span>Vis på alle skjermer</span>
                </label>
              </div>

              {!showAllScreens && (
                <div className="screens-list">
                  {screens.map(screen => (
                    <div key={screen.id} className="screen-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedScreens.includes(screen.id)}
                          onChange={() => handleScreenToggle(screen.id)}
                        />
                        <span>{screen.screen_name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <small>Velg hvilke oppganger/skjermer denne meldingen skal vises på</small>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            data-testid="submit-button"
            disabled={loading}
          >
            {loading ? 'Sender...' : 'Send melding'}
          </button>
        </form>
      </div>
      
      <style>{`
        .submit-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #50E3C2 0%, #429C88 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
        }
        
        .submit-container {
          background: white;
          padding: 2rem;
          border-radius: 16px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        
        .submit-container h1 {
          color: #2A2A2A;
          margin-bottom: 0.5rem;
          font-size: 1.75rem;
        }
        
        .subtitle {
          color: #666;
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
        }
        
        .form-group {
          margin-bottom: 1.25rem;
        }
        
        .form-group label {
          display: block;
          color: #2A2A2A;
          margin-bottom: 0.5rem;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 0.65rem 0.85rem;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
          color: #2A2A2A;
          background: white;
        }
        
        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #50E3C2;
        }
        
        .form-group small {
          color: #666;
          font-size: 0.8rem;
          margin-top: 0.25rem;
          display: block;
        }
        
        .screen-checkbox {
          margin: 0.5rem 0;
        }
        
        .screen-checkbox label {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-weight: normal;
        }
        
        .screen-checkbox input[type="checkbox"] {
          width: auto;
          margin-right: 0.5rem;
          cursor: pointer;
        }
        
        .screens-list {
          padding-left: 1.5rem;
          margin-top: 0.5rem;
          border-left: 2px solid #e0e0e0;
        }

        .btn {
          width: 100%;
          padding: 0.85rem;
          font-size: 1rem;
          margin-top: 0.5rem;
        }
        
        @media (max-width: 768px) {
          .submit-page {
            padding: 0.75rem;
            align-items: flex-start;
            min-height: 100vh;
            overflow-y: auto;
          }

          .submit-container {
            padding: 1rem;
            margin: 0;
            max-height: none;
          }
          
          .submit-container h1 {
            font-size: 1.35rem;
            margin-bottom: 0.35rem;
          }

          .subtitle {
            margin-bottom: 1rem;
            font-size: 0.85rem;
          }

          .form-group {
            margin-bottom: 0.85rem;
          }

          .form-group label {
            margin-bottom: 0.35rem;
            font-size: 0.85rem;
          }

          .form-group input,
          .form-group textarea {
            padding: 0.55rem 0.7rem;
            font-size: 0.9rem;
          }

          .form-group textarea {
            min-height: 80px;
          }

          .form-group small {
            font-size: 0.75rem;
            margin-top: 0.2rem;
          }

          .screen-checkbox {
            margin: 0.35rem 0;
          }

          .screen-checkbox label {
            font-size: 0.85rem;
          }

          .screens-list {
            padding-left: 1rem;
            margin-top: 0.35rem;
          }

          .btn {
            padding: 0.7rem;
            font-size: 0.95rem;
            margin-top: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .submit-page {
            padding: 0.5rem;
          }

          .submit-container {
            padding: 0.85rem;
            border-radius: 12px;
          }

          .submit-container h1 {
            font-size: 1.2rem;
            margin-bottom: 0.25rem;
          }

          .subtitle {
            margin-bottom: 0.75rem;
            font-size: 0.8rem;
          }

          .form-group {
            margin-bottom: 0.7rem;
          }

          .form-group label {
            margin-bottom: 0.25rem;
            font-size: 0.8rem;
          }

          .form-group input,
          .form-group textarea {
            padding: 0.5rem 0.65rem;
            font-size: 0.85rem;
          }

          .form-group textarea {
            min-height: 70px;
          }

          .btn {
            padding: 0.65rem;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SubmitMessage;
